import ActionButtonComponent from "@ui-components/ActionButtonComponent";
import ArrowDownTrayIcon from "@heroicons/react/24/solid/ArrowDownTrayIcon";
import BarsArrowDownIcon from "@heroicons/react/24/solid/BarsArrowDownIcon";
import {downloadZip} from "./FilesAndS3Utils";
import {prodGrouping} from "./constants";
import _ from "lodash";

export const buildProductsDocuments = (
	products,
	intl,
	apiRequest,
	productsDocumentsGrouping,
	setProductsDocumentsGrouping,
	search
) => {

	const prepareAndDownloadZip = async (docs) => {
		const promises = docs.map(async (document) => {
			let blob;
			try {
				const payload = {
					url: document.link,
					expected_content_type: "application/pdf",
				};
				blob = await apiRequest.post(`/products/document`, payload, {responseType: "blob"});
			} catch (e) {
				console.warn(`Error downloading document "${document.name}" - skipping`);
				console.warn(e);
				return;
			}
			return {filename: document.name + ".pdf", blob};
		})
		await downloadZip("products", await Promise.all(promises))
	}

	const getSchedaTecnicaName = (link) => {
		const splitted = link.split("/")
		return splitted[splitted.length - 1]
	}

	/*
	 * groupProductsBy groups the products documents by the selected grouping
	 * @param grouping: the grouping to use
	 * @return an object with the grouped documents
	 * this function is called every time the grouping changes
	 * can group by product or by document type.
	 */
	const groupProductsBy = (grouping) => {
		const filteredProducts = products?.filter((p) => ["des_prod_comm", "art_des_it"].some((field) => {
			const value = p[field] || "";
			return value.toLowerCase().includes(search.toLowerCase());
		})) ?? [];
		switch (grouping) {
			case prodGrouping.PRODUCT:
				return {
					title: intl.formatMessage({id: "technical_documents"}),
					alternateColor: "bg-gray-100",
					groups: filteredProducts
						.filter(p => p.link_scheda_tecnica)
						.map((p) => ({
							key: "products",
							title: p.label,
							documents: Object.keys(p)
								.filter((key) => key.startsWith("link_"))
								.reduce((acc, key) => {
									const label = _.upperCase(
										key.replace("link_", "").replace(/_/g, " ")
									);
									const urls = (p[key] || "")
										.split(",")
										.map(url => url.trim())
										.filter(url => url !== "" && url.startsWith("http"));
									if (urls.length === 0)
										return acc;
									const docs = urls.map((u) => ({
										name: decodeURIComponent(new URL(u).pathname.split('/').pop()) + ` (${label})`,
										link: u,
										onDocumentClicked: () => {
											window.open(u, "_blank");
										},
										visible: true,
									}));
									return [...acc, ...docs]
								}, []),
							})
						),
				}
			case prodGrouping.DOCUMENT_TYPE:
				return {
					title: intl.formatMessage({id: "technical_documents"}),
					alternateColor: "bg-gray-100",
					groups: [
						"link_scheda_tecnica", "link_video",
						"link_dop", "link_cam", "link_epd", "link_sds", "link_schede_sicurezza", "link_voc"
					].reduce((acc, docTypeKey) => {
						const label = _.capitalize(
							docTypeKey.replace("link_", "").replace(/_/g, " ")
						);
						const obj = {
							key: docTypeKey,
							title: label,
							documents: filteredProducts
								.reduce((acc, product) => {
									const urls = (product[docTypeKey] || "")
										.split(",")
										.map(url => url.trim())
										.filter(url => url !== "" && url.startsWith("http"));
									if (urls.length === 0)
										return acc;
									const docs = urls.map((u) => ({
										name: `${product.label} - ${decodeURIComponent(new URL(u).pathname.split('/').pop())}`,
										link: u,
										onDocumentClicked: () => {
											window.open(u, "_blank");
										},
										visible: true,
									}));
									return [...acc, ...docs];
								}, []),
						};
						if (obj.documents.length === 0)
							return acc;
						return [...acc, obj];
					}, []),
				}
			default:
				throw new Error("Invalid grouping")
		}
	}

	return {
		...groupProductsBy(productsDocumentsGrouping), // the documents grouped by the selected grouping
		// the actions to show in the page
		actions: {
			download:	{
				// download all the documents as a zip
				actionComponent: (docs, visible) => <ActionButtonComponent
					icon={ArrowDownTrayIcon}
					action={async () => {
						await prepareAndDownloadZip(docs)
					}}
				/>
			},
			regroup:{
					// change the grouping of the documents
					actionComponent: (docs, visible) => <ActionButtonComponent
						icon={BarsArrowDownIcon}
						action={() => {
							if (productsDocumentsGrouping === prodGrouping.PRODUCT) {
								setProductsDocumentsGrouping(prodGrouping.DOCUMENT_TYPE)
							} else {
								setProductsDocumentsGrouping(prodGrouping.PRODUCT)
							}
						}}
					/>
				}
		}
	}
}